import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatDate, type Course, type CourseEdition, type Enrollment } from "@/lib/api";
import SignatureUpload from "@/components/feature/SignatureUpload";
import ListingFilters from "@/components/feature/ListingFilters";
import { emptyListingFilters, matchesMonth, type ListingFilterState } from "@/lib/listingFilters";

export default function InstructorPortalPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [editions, setEditions] = useState<CourseEdition[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<ListingFilterState>(emptyListingFilters);
  const [instructorFilter, setInstructorFilter] = useState("");
  const [evaluatorFilter, setEvaluatorFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [editionsData, enrollmentsData] = await Promise.all([
          api.getEditions(),
          api.getEnrollments(),
        ]);
        setEditions(editionsData);
        setEnrollments(enrollmentsData);
      } catch {
        setEditions([]);
        setEnrollments([]);
      }
      setLoading(false);
    })();
  }, []);

  const courses = useMemo(() => {
    const map = new Map<string, Course>();
    for (const ed of editions) {
      if (ed.course && !map.has(ed.course.id)) map.set(ed.course.id, ed.course);
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, "es"));
  }, [editions]);

  const instructors = useMemo(() => {
    const map = new Map<string, string>();
    for (const ed of editions) {
      if (ed.instructor_id && ed.instructor?.full_name) {
        map.set(ed.instructor_id, ed.instructor.full_name);
      }
    }
    return Array.from(map.entries())
      .map(([id, full_name]) => ({ id, full_name }))
      .sort((a, b) => a.full_name.localeCompare(b.full_name, "es"));
  }, [editions]);

  const evaluators = useMemo(() => {
    const map = new Map<string, string>();
    for (const ed of editions) {
      if (ed.evaluator_id && ed.evaluator?.full_name) {
        map.set(ed.evaluator_id, ed.evaluator.full_name);
      }
    }
    return Array.from(map.entries())
      .map(([id, full_name]) => ({ id, full_name }))
      .sort((a, b) => a.full_name.localeCompare(b.full_name, "es"));
  }, [editions]);

  const filteredEditions = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return editions.filter((ed) => {
      if (filters.courseId && ed.course_id !== filters.courseId) return false;
      if (filters.editionId && ed.id !== filters.editionId) return false;
      if (filters.category && ed.course?.category !== filters.category) return false;
      if (filters.month && !matchesMonth(ed.start_date, filters.month)) return false;
      if (instructorFilter && ed.instructor_id !== instructorFilter) return false;
      if (evaluatorFilter && ed.evaluator_id !== evaluatorFilter) return false;
      if (statusFilter && ed.status !== statusFilter) return false;
      if (q) {
        const haystack = [
          ed.course?.name,
          ed.edition_label,
          ed.location,
          ed.instructor?.full_name,
          ed.evaluator?.full_name,
          ed.course?.category,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [editions, filters, instructorFilter, evaluatorFilter, statusFilter]);

  const hasExtraFilters = Boolean(instructorFilter || evaluatorFilter || statusFilter);

  const enrollmentEditionId = (enrollment: Enrollment) => enrollment.edition_id || enrollment.diplomado_edition_id || "";

  const getEditionEnrollments = (editionId: string) =>
    enrollments.filter((e) => enrollmentEditionId(e) === editionId);

  const getAttendanceDone = (editionId: string) =>
    getEditionEnrollments(editionId).filter((e) => e.attendance_status !== "pendiente").length;

  const totalAttendancePending = filteredEditions.reduce((sum, ed) => {
    const total = getEditionEnrollments(ed.id).length;
    const done = getAttendanceDone(ed.id);
    return sum + (total - done);
  }, 0);

  const totalAttendanceDone = filteredEditions.reduce((sum, ed) => sum + getAttendanceDone(ed.id), 0);

  const totalStudents = (() => {
    const editionIds = new Set(filteredEditions.map((e) => e.id));
    return enrollments.filter((e) => editionIds.has(enrollmentEditionId(e))).length;
  })();

  const editionStatusBadge = (status: string) => {
    switch (status) {
      case "en_curso":
        return <span className="badge-accent flex-shrink-0">En Curso</span>;
      case "completado":
        return <span className="badge-success flex-shrink-0">Completado</span>;
      case "cancelado":
        return <span className="badge-danger flex-shrink-0">Cancelado</span>;
      default:
        return <span className="badge-info flex-shrink-0">Programado</span>;
    }
  };

  const hasFilters =
    Boolean(filters.search.trim() || filters.courseId || filters.editionId || filters.category || filters.month) ||
    hasExtraFilters;

  return (
    <div className="space-y-6 animate-[fadeIn_0.3s_ease-out]">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">Portal del Instructor</h1>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">
            Bienvenido, {user?.full_name} &mdash; Gestione sus ediciones y asistencia
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[var(--color-accent-oil)]/15 border border-[var(--color-accent-oil)]/20 flex items-center justify-center flex-shrink-0">
            <i className="ri-book-open-line text-[var(--color-accent-oil)] text-xl"></i>
          </div>
          <div>
            <p className="text-2xl font-bold text-[var(--color-text-primary)]">{filteredEditions.length}</p>
            <p className="text-xs text-[var(--color-text-muted)]">Ediciones{hasFilters ? " (filtro)" : " Asignadas"}</p>
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-yellow-500/15 border border-yellow-500/20 flex items-center justify-center flex-shrink-0">
            <i className="ri-user-follow-line text-yellow-400 text-xl"></i>
          </div>
          <div>
            <p className="text-2xl font-bold text-yellow-400">{totalAttendancePending}</p>
            <p className="text-xs text-[var(--color-text-muted)]">Asist. Pendientes</p>
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-green-500/15 border border-green-500/20 flex items-center justify-center flex-shrink-0">
            <i className="ri-check-line text-green-400 text-xl"></i>
          </div>
          <div>
            <p className="text-2xl font-bold text-green-400">{totalAttendanceDone}</p>
            <p className="text-xs text-[var(--color-text-muted)]">Asist. Registradas</p>
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[var(--color-bg-tertiary)] flex items-center justify-center flex-shrink-0">
            <i className="ri-group-line text-[var(--color-text-muted)] text-xl"></i>
          </div>
          <div>
            <p className="text-2xl font-bold text-[var(--color-text-primary)]">{totalStudents}</p>
            <p className="text-xs text-[var(--color-text-muted)]">Total Estudiantes</p>
          </div>
        </div>
      </div>

      <SignatureUpload />

      {!loading && editions.length > 0 && (
        <ListingFilters
          title="Filtros de ediciones"
          filters={filters}
          onChange={setFilters}
          courses={courses}
          editions={editions}
          searchPlaceholder="Curso, edición, persona..."
          resultCount={filteredEditions.length}
          totalCount={editions.length}
          hint="Mes filtra por fecha de inicio de la edición."
          extraActive={hasExtraFilters}
          onClearExtras={() => {
            setInstructorFilter("");
            setEvaluatorFilter("");
            setStatusFilter("");
          }}
          extras={
            <>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1 block">Instructor</label>
                <select
                  className="input-field"
                  value={instructorFilter}
                  onChange={(e) => setInstructorFilter(e.target.value)}
                  aria-label="Filtrar por instructor"
                >
                  <option value="">Todos los instructores</option>
                  {instructors.map((i) => (
                    <option key={i.id} value={i.id}>{i.full_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1 block">Evaluador</label>
                <select
                  className="input-field"
                  value={evaluatorFilter}
                  onChange={(e) => setEvaluatorFilter(e.target.value)}
                  aria-label="Filtrar por evaluador"
                >
                  <option value="">Todos los evaluadores</option>
                  {evaluators.map((e) => (
                    <option key={e.id} value={e.id}>{e.full_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1 block">Estado</label>
                <select
                  className="input-field"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  aria-label="Filtrar por estado"
                >
                  <option value="">Todos los estados</option>
                  <option value="programado">Programado</option>
                  <option value="en_curso">En curso</option>
                  <option value="completado">Completado</option>
                  <option value="cancelado">Cancelado</option>
                </select>
              </div>
            </>
          }
        />
      )}

      {loading ? (
        <div className="card text-center py-16">
          <i className="ri-loader-4-line text-3xl text-[var(--color-text-muted)] animate-spin"></i>
          <p className="text-sm text-[var(--color-text-muted)] mt-3">Cargando ediciones...</p>
        </div>
      ) : editions.length === 0 ? (
        <div className="card text-center py-16">
          <i className="ri-presentation-line text-4xl text-[var(--color-text-muted)]"></i>
          <p className="text-sm text-[var(--color-text-muted)] mt-3">
            No tiene ediciones asignadas como instructor actualmente.
          </p>
        </div>
      ) : filteredEditions.length === 0 ? (
        <div className="card text-center py-16">
          <i className="ri-filter-off-line text-4xl text-[var(--color-text-muted)]"></i>
          <p className="text-sm text-[var(--color-text-muted)] mt-3">
            Ninguna edición coincide con los filtros.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredEditions.map((edition) => {
            const total = getEditionEnrollments(edition.id).length;
            const done = getAttendanceDone(edition.id);
            const progress = total > 0 ? Math.round((done / total) * 100) : 0;
            const courseName = edition.course?.name || "Curso";

            return (
              <div
                key={edition.id}
                onClick={() => navigate(`/portal-instructor/curso/${edition.id}`)}
                className="card hover:border-[var(--color-border-light)] cursor-pointer group transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-sm font-semibold text-[var(--color-text-primary)] line-clamp-2 flex-1 pr-2">
                    {courseName}
                  </h3>
                  {editionStatusBadge(edition.status)}
                </div>

                {edition.edition_label && (
                  <p className="text-xs text-[var(--color-text-accent)] mb-2">{edition.edition_label}</p>
                )}

                <div className="space-y-1.5 mb-3">
                  <p className="text-xs text-[var(--color-text-secondary)] flex items-center gap-1.5">
                    <i className="ri-user-star-line text-[var(--color-accent-oil)]"></i>
                    <span className="text-[var(--color-text-muted)]">Instructor:</span>
                    <span className="truncate">{edition.instructor?.full_name || "Sin asignar"}</span>
                  </p>
                  <p className="text-xs text-[var(--color-text-secondary)] flex items-center gap-1.5">
                    <i className="ri-user-follow-line text-[var(--color-accent)]"></i>
                    <span className="text-[var(--color-text-muted)]">Evaluador:</span>
                    <span className="truncate">{edition.evaluator?.full_name || "Sin asignar"}</span>
                  </p>
                </div>

                <div className="flex items-center gap-3 text-xs text-[var(--color-text-muted)] mb-4">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {formatDate(edition.start_date)}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-map-pin-line"></i>
                    {edition.location.split(" - ")[0]}
                  </span>
                </div>

                <div className="border-t border-[var(--color-border)] pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-[var(--color-text-muted)]">Asistencia</span>
                    <span className="text-xs font-medium text-[var(--color-text-primary)]">{done}/{total}</span>
                  </div>
                  <div className="w-full h-2 bg-[var(--color-bg-tertiary)] rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        progress >= 80 ? "bg-green-400" : progress >= 40 ? "bg-[var(--color-accent-oil)]" : "bg-yellow-400"
                      }`}
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-[var(--color-text-muted)]">{progress}% completado</span>
                    <span className="text-xs text-[var(--color-text-accent)] group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                      Gestionar <i className="ri-arrow-right-s-line"></i>
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
